import { useState, useEffect, useRef, useCallback } from 'react'
import styled, { keyframes } from 'styled-components'
import { api, CandidateRecommendation, RecommendationResponse } from '../../../services/api'
import { Input, Select } from '../../../components/shared/StyledComponents'
import { ScoreBreakdownCard } from '../../../components/shared/ScoreBreakdownCard'
import { recordRecommendationAssigned } from './recordRecommendationDecision'

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const slideIn = keyframes`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`

const Modal = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  width: 95%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideIn} 0.3s ease-out;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
  position: sticky;
  top: 0;
  z-index: 10;
`

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.lg};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`

const FiltersSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
`

const SearchButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  font-weight: 500;
  align-self: end;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.border};
    cursor: not-allowed;
  }
`

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: linear-gradient(135deg, #E3F2FD, #BBDEFB);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const InfoItem = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`

const CandidatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`

interface TierBadgeProps {
    tier: number
}

const TierBadge = styled.span<TierBadgeProps>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  color: white;
  background: ${({ tier }) => {
        switch (tier) {
            case 1: return 'linear-gradient(135deg, #2E7D32, #43A047)'
            case 2: return 'linear-gradient(135deg, #1565C0, #1E88E5)'
            case 3: return 'linear-gradient(135deg, #7B1FA2, #9C27B0)'
            case 4: return 'linear-gradient(135deg, #EF6C00, #FF9800)'
            case 5: return 'linear-gradient(135deg, #C62828, #E53935)'
            default: return '#9E9E9E'
        }
    }};
`

const CandidateCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`

const CandidateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.secondary};
`

const CandidateInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`

const CandidateName = styled.span`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.md};
`

const CandidateRole = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const ScoreDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`

const Score = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`

const CandidateBody = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
`

const MotivosSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

interface MotivoChipProps {
    tipo: string
}

const MotivoChip = styled.span<MotivoChipProps>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  background: ${({ tipo, theme }) => {
        switch (tipo) {
            case 'tecnologia': return '#E8F5E9'
            case 'sector': return '#E3F2FD'
            case 'experiencia': return '#FFF3E0'
            case 'disponibilidad': return '#F3E5F5'
            case 'penalizacion': return '#FFEBEE'
            default: return theme.colors.secondary
        }
    }};
  color: ${({ tipo }) => {
        switch (tipo) {
            case 'tecnologia': return '#2E7D32'
            case 'sector': return '#1565C0'
            case 'experiencia': return '#EF6C00'
            case 'disponibilidad': return '#7B1FA2'
            case 'penalizacion': return '#C62828'
            default: return '#666'
        }
    }};
`

const StatsRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`

const StatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const AssignmentsSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
`

const AssignmentChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  background: #FFF8E1;
  color: #F57F17;
  margin-right: ${({ theme }) => theme.spacing.xs};
`

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`

const AssignButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`

const BreakdownToggle = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
  }
`

const BreakdownSection = styled.div<{ isOpen: boolean }>`
  max-height: ${({ isOpen }) => isOpen ? '600px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`

const VersionBadge = styled.span`
  background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: auto;
`

const RefreshIndicator = styled.span`
  color: ${({ theme }) => theme.colors.success};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  display: flex;
  align-items: center;
  gap: 4px;
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textMuted};
`

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  gap: ${({ theme }) => theme.spacing.md};
`

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${({ theme }) => theme.colors.secondary};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`

interface RecommendationModalProps {
    isOpen: boolean
    onClose: () => void
    opportunityId: number
    opportunityName: string
    onAssign?: (userId: number, displayName: string, rol: string, cargabilidad: number) => void
    roles?: string[]
    /** The opportunity's role requirements: a picked role brings its dedication, technologies and deferred start. */
    roleRequirements?: Array<{
        role: string
        cargabilidad?: number
        tecnologias?: string[]
        start_availability?: string
    }>
    initialRole?: string
    initialCargabilidad?: number
}

export const RecommendationModal: React.FC<RecommendationModalProps> = ({
    isOpen,
    onClose,
    opportunityId,
    opportunityName,
    onAssign,
    roles = [],
    roleRequirements = [],
    initialRole = '',
    initialCargabilidad = 100,
}) => {
    const [loading, setLoading] = useState(false)
    const [response, setResponse] = useState<RecommendationResponse | null>(null)
    const [error, setError] = useState<string | null>(null)

    // Filter state - initialized from props
    const [rol, setRol] = useState(initialRole)
    const [cargabilidad, setCargabilidad] = useState(initialCargabilidad)
    const [fechaInicio, setFechaInicio] = useState('')
    const [fechaFin, setFechaFin] = useState('')

    // Breakdown visibility state (per candidate)
    const [expandedBreakdowns, setExpandedBreakdowns] = useState<Set<number>>(new Set())

    // Version polling state
    const [currentVersion, setCurrentVersion] = useState<number>(0)
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true)
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

    // Update state when initial values change
    useEffect(() => {
        setRol(initialRole)
        setCargabilidad(initialCargabilidad)
    }, [initialRole, initialCargabilidad])

    const requirementFor = (role: string) => roleRequirements.find((req) => req.role === role)
    const requirement = requirementFor(rol)

    // Picking a role adopts its required dedication (still editable): assigning
    // from here used to add the person at 100% whatever the role asked for.
    const handleRoleChange = (role: string) => {
        setRol(role)
        const cargabilidadRol = requirementFor(role)?.cargabilidad
        if (cargabilidadRol !== undefined) setCargabilidad(cargabilidadRol)
    }

    const fetchRecommendations = useCallback(async () => {
        setLoading(true)
        setError(null)

        try {
            const result = await api.recommendations.getCandidates({
                opportunity_id: opportunityId,
                rol: rol || 'General',
                cargabilidad,
                fecha_inicio: fechaInicio || undefined,
                fecha_fin: fechaFin || undefined,
                // Same role inputs the inline recommender sends, so both rank alike.
                tecnologias: requirement?.tecnologias || undefined,
                start_availability: requirement?.start_availability || undefined,
            })
            setResponse(result.data)

            // Update version from response if available
            if (result.data.version) {
                setCurrentVersion(result.data.version)
            }
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Error al obtener recomendaciones')
        } finally {
            setLoading(false)
        }
    }, [opportunityId, rol, cargabilidad, fechaInicio, fechaFin, requirement?.tecnologias, requirement?.start_availability])

    // Version polling for auto-refresh
    const checkVersion = useCallback(async () => {
        if (!isOpen || !autoRefreshEnabled) return

        try {
            const versionResult = await api.recommendations.getVersion(currentVersion)
            if (versionResult.data.is_stale) {
                // Recommendations are stale, refresh them
                console.log('Recommendations stale, auto-refreshing...')
                setCurrentVersion(versionResult.data.version)
                fetchRecommendations()
            }
        } catch (err) {
            // Silently ignore version check errors
            console.debug('Version check failed:', err)
        }
    }, [isOpen, autoRefreshEnabled, currentVersion, fetchRecommendations])

    // Set up polling interval
    useEffect(() => {
        if (isOpen && autoRefreshEnabled) {
            // Poll every 15 seconds
            pollingIntervalRef.current = setInterval(checkVersion, 15000)
        }

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current)
                pollingIntervalRef.current = null
            }
        }
    }, [isOpen, autoRefreshEnabled, checkVersion])

    useEffect(() => {
        if (isOpen && opportunityId) {
            fetchRecommendations()
        }
    }, [isOpen, opportunityId, initialRole, initialCargabilidad])

    const handleAssign = (candidate: CandidateRecommendation) => {
        if (onAssign) {
            onAssign(candidate.user_id, candidate.display_name, rol || candidate.job_role || '', cargabilidad)
            void recordRecommendationAssigned(opportunityId, candidate.user_id, candidate.history_id)
            // Trigger immediate version check after assignment
            setTimeout(checkVersion, 500)
            onClose()  // Close modal after assigning
        }
    }

    const toggleBreakdown = (userId: number) => {
        setExpandedBreakdowns(prev => {
            const newSet = new Set(prev)
            if (newSet.has(userId)) {
                newSet.delete(userId)
            } else {
                newSet.add(userId)
            }
            return newSet
        })
    }

    const getTierIcon = (tier: number) => {
        switch (tier) {
            case 1: return '🎯'
            case 2: return '⭐'
            case 3: return '✓'
            case 4: return '~'
            case 5: return '⚠️'
            default: return ''
        }
    }

    const getMotivoIcon = (tipo: string) => {
        switch (tipo) {
            case 'tecnologia': return '💻'
            case 'sector': return '🏢'
            case 'experiencia': return '📊'
            case 'disponibilidad': return '📅'
            case 'penalizacion': return '⚠️'
            default: return '•'
        }
    }

    if (!isOpen) return null

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <ModalTitle>🎯 Recomendar Candidatos - {opportunityName}</ModalTitle>
                    {autoRefreshEnabled && (
                        <RefreshIndicator>
                            🔄 Auto-refresh activo
                        </RefreshIndicator>
                    )}
                    <CloseButton onClick={onClose}>&times;</CloseButton>
                </ModalHeader>

                <ModalBody>
                    <FiltersSection>
                        <FilterGroup>
                            <Label>Rol Requerido</Label>
                            <Select value={rol} onChange={(e) => handleRoleChange(e.target.value)}>
                                <option value="">Todos los roles</option>
                                {roles.map((r) => (
                                    <option key={r} value={r}>{r}</option>
                                ))}
                            </Select>
                        </FilterGroup>

                        <FilterGroup>
                            <Label>Cargabilidad (%)</Label>
                            <Input
                                type="number"
                                min="0"
                                max="100"
                                value={cargabilidad}
                                onChange={(e) => setCargabilidad(parseInt(e.target.value) || 100)}
                            />
                        </FilterGroup>

                        <FilterGroup>
                            <Label>Fecha Inicio</Label>
                            <Input
                                type="date"
                                value={fechaInicio}
                                onChange={(e) => setFechaInicio(e.target.value)}
                            />
                        </FilterGroup>

                        <FilterGroup>
                            <Label>Fecha Fin</Label>
                            <Input
                                type="date"
                                value={fechaFin}
                                onChange={(e) => setFechaFin(e.target.value)}
                            />
                        </FilterGroup>

                        <SearchButton onClick={fetchRecommendations} disabled={loading}>
                            {loading ? 'Buscando...' : '🔍 Buscar'}
                        </SearchButton>
                    </FiltersSection>

                    {response && (
                        <InfoBanner>
                            <InfoItem>
                                <strong>📊 Probabilidad:</strong> {response.probabilidad_oferta || 'N/A'}
                                {response.probabilidad_oferta && ` (factor: ${(response.factor_probabilidad * 100).toFixed(0)}%)`}
                            </InfoItem>
                            <InfoItem>
                                <strong>📋 Asignación Efectiva:</strong> {response.asignacion_efectiva.toFixed(1)}%
                            </InfoItem>
                            <InfoItem>
                                <strong>👥 Candidatos:</strong> {response.total_candidatos}
                            </InfoItem>
                            <VersionBadge>v{currentVersion}</VersionBadge>
                        </InfoBanner>
                    )}

                    {/* Rebalancing suggestion alert (orange) */}
                    {response?.rebalanceo_sugerido && (
                        <div style={{
                            background: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
                            border: '1px solid #FF9800',
                            borderRadius: '8px',
                            padding: '16px',
                            margin: '16px 0',
                        }}>
                            <div style={{ fontWeight: 600, color: '#E65100', marginBottom: '8px', fontSize: '1rem' }}>
                                🔄 Rebalanceo Sugerido
                            </div>
                            <div style={{ fontSize: '0.9em', color: '#BF360C' }}>
                                {response.capacidad_insuficiente_mensaje}
                            </div>
                            {response.resumen_capacidad && (
                                <div style={{ marginTop: '8px', fontSize: '0.8em', color: '#666' }}>
                                    <strong>Resumen:</strong> {response.resumen_capacidad.con_perfil_adecuado} candidatos con perfil exacto,
                                    {' '}{response.resumen_capacidad.con_perfil_parcial || 0} con perfil parcial,
                                    {' '}{response.resumen_capacidad.al_100_cargabilidad} al 100% cargabilidad
                                </div>
                            )}
                        </div>
                    )}

                    {/* Hiring need alert (red) - only when rebalancing is NOT suggested */}
                    {response?.necesita_contratacion && !response?.rebalanceo_sugerido && (
                        <div style={{
                            background: 'linear-gradient(135deg, #FFEBEE, #FFCDD2)',
                            border: '1px solid #EF5350',
                            borderRadius: '8px',
                            padding: '16px',
                            margin: '16px 0',
                        }}>
                            <div style={{ fontWeight: 600, color: '#C62828', marginBottom: '8px', fontSize: '1rem' }}>
                                ⚠️ Necesidad de Contratación
                            </div>
                            <div style={{ fontSize: '0.9em', color: '#B71C1C' }}>
                                {response.capacidad_insuficiente_mensaje}
                            </div>
                            {response.resumen_capacidad && (
                                <div style={{ marginTop: '8px', fontSize: '0.8em', color: '#666' }}>
                                    <strong>Resumen:</strong> {response.resumen_capacidad.total_evaluados} candidatos evaluados,
                                    {' '}ninguno con perfil técnico adecuado
                                </div>
                            )}
                        </div>
                    )}

                    {loading ? (
                        <LoadingState>
                            <Spinner />
                            <span>Analizando candidatos...</span>
                        </LoadingState>
                    ) : error ? (
                        <EmptyState>
                            <p>❌ {error}</p>
                        </EmptyState>
                    ) : response?.candidatos.length === 0 ? (
                        <EmptyState>
                            {response?.rebalanceo_sugerido ? (
                                <p>🔄 Hay candidatos con perfil adecuado, pero están ocupados. Considere redistribuir cargas.</p>
                            ) : response?.necesita_contratacion ? (
                                <p>⚠️ No se encontraron candidatos con el perfil técnico ni habilidades relacionadas.</p>
                            ) : (
                                <>
                                    <p>😕 No se encontraron candidatos que cumplan los criterios.</p>
                                    <p>Prueba a relajar los filtros o reducir la cargabilidad requerida.</p>
                                </>
                            )}
                        </EmptyState>
                    ) : (
                        <CandidatesList>
                            {response?.candidatos.map((candidate) => (
                                <CandidateCard key={candidate.user_id}>
                                    <CandidateHeader>
                                        <CandidateInfo>
                                            <TierBadge tier={candidate.tier}>
                                                {getTierIcon(candidate.tier)} {candidate.tier_nombre}
                                            </TierBadge>
                                            <CandidateName>{candidate.display_name}</CandidateName>
                                            <CandidateRole>{candidate.job_role}</CandidateRole>
                                        </CandidateInfo>
                                        <ScoreDisplay>
                                            <Score>{candidate.score_final.toFixed(1)}</Score>
                                            <span style={{ fontSize: '0.75rem', color: '#666' }}>puntos</span>
                                        </ScoreDisplay>
                                    </CandidateHeader>

                                    <CandidateBody>
                                        <MotivosSection>
                                            {candidate.motivos.map((motivo, idx) => (
                                                <MotivoChip key={idx} tipo={motivo.tipo}>
                                                    {getMotivoIcon(motivo.tipo)} {motivo.descripcion}
                                                </MotivoChip>
                                            ))}
                                        </MotivosSection>

                                        <StatsRow>
                                            <StatItem>
                                                📅 Disponibilidad: <strong>{candidate.disponibilidad_periodo.toFixed(0)}%</strong>
                                            </StatItem>
                                            <StatItem>
                                                📊 Experiencia: <strong>{candidate.years_experience || 0} años</strong>
                                            </StatItem>
                                            {candidate.overlap_pct > 0 && (
                                                <StatItem style={{ color: '#C62828' }}>
                                                    ⚠️ Overlap: <strong>{candidate.overlap_pct.toFixed(0)}%</strong>
                                                </StatItem>
                                            )}
                                            {candidate.penalizacion_reasignacion > 0 && (
                                                <StatItem style={{ color: '#EF6C00' }}>
                                                    🔄 Penalización: <strong>-{(candidate.penalizacion_reasignacion * 100).toFixed(0)}%</strong>
                                                </StatItem>
                                            )}
                                        </StatsRow>

                                        {/* Score Breakdown Toggle */}
                                        {candidate.score_breakdown && (
                                            <>
                                                <BreakdownToggle onClick={() => toggleBreakdown(candidate.user_id)}>
                                                    {expandedBreakdowns.has(candidate.user_id) ? '▼' : '▶'}
                                                    Ver desglose del score
                                                </BreakdownToggle>
                                                <BreakdownSection isOpen={expandedBreakdowns.has(candidate.user_id)}>
                                                    <ScoreBreakdownCard
                                                        breakdown={candidate.score_breakdown}
                                                        compact={false}
                                                        explainContext={{
                                                            candidateName: candidate.display_name,
                                                            candidateRole: candidate.job_role,
                                                            candidateYears: candidate.years_experience,
                                                            requestedRole: response?.rol_solicitado || rol || 'Rol',
                                                            opportunityName: opportunityName,
                                                            motivos: candidate.motivos,
                                                        }}
                                                    />
                                                </BreakdownSection>
                                            </>
                                        )}

                                        {candidate.asignaciones_actuales.length > 0 && (
                                            <AssignmentsSection>
                                                <Label>Proyectos actuales:</Label>
                                                <div style={{ marginTop: '0.5rem' }}>
                                                    {candidate.asignaciones_actuales.map((asig, idx) => (
                                                        <AssignmentChip key={idx}>
                                                            {asig.opportunity_name} ({asig.cargabilidad}%)
                                                        </AssignmentChip>
                                                    ))}
                                                </div>
                                            </AssignmentsSection>
                                        )}

                                        <ActionButtons>
                                            <AssignButton onClick={() => handleAssign(candidate)}>
                                                ✓ Asignar al proyecto
                                            </AssignButton>
                                        </ActionButtons>
                                    </CandidateBody>
                                </CandidateCard>
                            ))}
                        </CandidatesList>
                    )}
                </ModalBody>
            </Modal>
        </Overlay>
    )
}

export default RecommendationModal
